import React, { memo } from 'react';
import { FixedSizeList } from 'react-window';

/**
 * Virtual scrolling list component for detailed call views (with transcripts, summaries, etc.)
 * Used in Mvp.js and similar pages
 */
const VirtualCallDetailList = ({
  calls = [],
  getRecordingUrl,
  height = 600,
  itemHeight = 400, // Larger height for detailed items
  hasMore = false,
  loading = false,
  observerTarget = null
}) => {
  const CallDetailItem = memo(({ index, style, data }) => {
    const call = data.calls[index];
    if (!call) return null;

    return (
      <div style={style}>
        <div className="p-4 mx-2 mb-5 border border-gray-200 rounded-lg bg-gray-50">
          {/* Header */}
          <div className="flex justify-between">
            <div>
              <div>
                <b>From:</b> {call.from || "Unknown"} &nbsp; | &nbsp;
                <b>To:</b> {call.to || "Unknown"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-gray-600 text-xs">
                {new Date(call.createdAt || call.created_at).toLocaleString()}
              </div>
              <div className="mt-1 text-xs">{call.callSid}</div>
            </div>
          </div>

          {/* Transcript */}
          <div className="mt-4">
            <b>Transcript:</b>
            <div className="mt-3">
              {call?.transcript?.length > 0 ? (
                call.transcript.map((t, idx) => (
                  <div
                    key={idx}
                    className={`flex mb-2 ${t.role === "agent" ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`max-w-[70%] px-3 py-2 rounded-xl text-sm text-gray-800 ${
                        t.role === "agent" ? "bg-blue-100" : "bg-green-100"
                      }`}
                    >
                      <b>{t.role === "agent" ? "🤖 Agent" : "👤 User"}:</b> {t.text}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-600">No transcript available</div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="mt-4">
            <b>Summary:</b>
            <div className="mt-2 text-gray-800">
              {call.summary || "No summary available"}
            </div>
          </div>

          {/* Recording */}
          <div className="mt-4">
            <div className="mb-4"><b>Recording:</b></div>
            {call.recording && data.getRecordingUrl ? (
              <audio controls className="w-full">
                <source
                  src={data.getRecordingUrl(call.callSid)}
                  type="audio/mpeg"
                />
                Your browser does not support audio playback.
              </audio>
            ) : (
              <div className="text-gray-600">Recording not available</div>
            )}
          </div>
        </div>
      </div>
    );
  });

  CallDetailItem.displayName = 'CallDetailItem';

  if (calls.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-600">
        No calls yet
      </div>
    );
  }

  return (
    <div className="relative">
      <FixedSizeList
        height={height}
        itemCount={calls.length}
        itemSize={itemHeight}
        width="100%"
        itemData={{
          calls,
          getRecordingUrl
        }}
        overscanCount={3}
      >
        {CallDetailItem}
      </FixedSizeList>
      
      {(hasMore || loading) && (
        <div 
          ref={observerTarget} 
          className="h-10 flex items-center justify-center py-4"
        >
          {loading && <div className="text-gray-500">Loading more calls...</div>}
        </div>
      )}
    </div>
  );
};

export default memo(VirtualCallDetailList);

